import React, { useState } from "react";
import { difficultySettings } from "./App";
import { ArrowLeft, Check } from "lucide-react";

const Checkbox = ({ enabled }) => (
  <div
    className={`w-6 h-6 rounded flex items-center justify-center transition-colors duration-200 border-2
      ${enabled ? "bg-white border-white" : "bg-transparent border-gray-500"}`}
  >
    {enabled && <Check size={16} className="text-blue-600" />}
  </div>
);

const GameInterface = ({ gameState, gameActions, handleAnswer }) => {
  const {
    gamePhase,
    categories,
    selectedCategories,
    selectedDifficulty,
    currentQuestion,
    shuffledQuestions,
  } = gameState;

  const [answerRevealed, setAnswerRevealed] = useState(false);

  const handleNext = () => {
    handleAnswer();
    setAnswerRevealed(false);
  };

  const NavigationHeader = ({ onBack, showBack = true }) => (
    <div className="flex justify-between items-center mb-8">
      <div className="flex items-center gap-4">
        <img src="/assets/logo.svg" alt="Logo" className="h-8 w-8" />
        <button
          onClick={() => gameActions.resetGame()}
          className="text-white/70 hover:text-white flex items-center gap-2"
        >
          <span>Home</span>
        </button>
      </div>
      {showBack && (
        <button
          onClick={onBack}
          className="text-white/70 hover:text-white flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
      )}
    </div>
  );

  if (gamePhase === "category") {
    return (
      <div className="min-h-screen bg-[#1a1f2e] p-8 pt-12 flex flex-col">
        <div className="w-full max-w-md mx-auto">
          <NavigationHeader onBack={() => gameActions.resetGame()} />
          <div className="mb-4">
            <h2 className="text-white text-2xl font-medium">
              Choose Categories
            </h2>
          </div>
          <div className="w-full border-b border-white/30 mb-8" />
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => gameActions.toggleAllCategories()}
              className={`flex items-center gap-4 w-full p-4 rounded-lg border-2 transition-all duration-200 text-left
                ${
                  selectedCategories.length === categories.length
                    ? "bg-blue-600 border-blue-400 text-white"
                    : "bg-[#2a2f3e] border-transparent text-white hover:bg-[#3a3f4e]"
                }`}
            >
              <Checkbox enabled={selectedCategories.length === categories.length} />
              <span className="text-lg">Select All</span>
            </button>
            {categories.map((cat) => {
              const isSelected = selectedCategories.includes(cat);

              return (
              <button
                type="button"
                key={cat}
                onClick={() => gameActions.selectCategory(cat)}
                className={`flex items-center gap-4 w-full p-4 rounded-lg border-2 transition-all duration-200 text-left
                  ${
                    isSelected
                      ? "bg-blue-600 border-blue-400 text-white"
                      : "bg-[#2a2f3e] border-transparent text-white hover:bg-[#3a3f4e]"
                  }`}
              >
                <Checkbox enabled={isSelected} />
                <span className="text-lg capitalize">
                  {cat}
                </span>
              </button>
            )})}
          </div>
          {selectedCategories.length > 0 && (
            <button
              onClick={() => gameActions.setGamePhase("difficulty")}
              className="w-full mt-8 py-4 rounded-lg bg-blue-600 text-white text-lg font-medium hover:bg-blue-700 transition-all duration-200"
            >
              Next
            </button>
          )}
        </div>
      </div>
    );
  }

  if (gamePhase === "difficulty") {
    return (
      <div className="min-h-screen bg-[#1a1f2e] p-8 pt-12 flex flex-col">
        <div className="w-full max-w-md mx-auto">
          <NavigationHeader
            onBack={() => gameActions.setGamePhase("category")}
          />
          <div className="mb-4">
            <h2 className="text-white text-2xl font-medium">
              Select Difficulties
            </h2>
          </div>
          <div className="w-full border-b border-white/30 mb-8" />
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => gameActions.toggleAllDifficulties()}
              className={`flex items-center gap-4 w-full p-4 rounded-lg border-2 transition-all duration-200 text-left
                ${
                  selectedDifficulty.length === Object.keys(difficultySettings).length
                    ? "bg-blue-600 border-blue-400 text-white"
                    : "bg-[#2a2f3e] border-transparent text-white hover:bg-[#3a3f4e]"
                }`}
            >
              <Checkbox enabled={selectedDifficulty.length === Object.keys(difficultySettings).length} />
              <span className="text-lg">Select All</span>
            </button>
            {Object.keys(difficultySettings).map((diff) => {
              const isSelected = selectedDifficulty.includes(diff);

              return (
              <button
                type="button"
                key={diff}
                onClick={() => gameActions.selectDifficulty(diff)}
                className={`flex items-center gap-4 w-full p-4 rounded-lg border-2 transition-all duration-200 text-left
                  ${
                    isSelected
                      ? "bg-blue-600 border-blue-400 text-white"
                      : "bg-[#2a2f3e] border-transparent text-white hover:bg-[#3a3f4e]"
                  }`}
              >
                <Checkbox enabled={isSelected} />
                <span className="text-lg capitalize">{diff}</span>
              </button>
            )})}
          </div>
          {selectedDifficulty.length > 0 && (
            <button
              onClick={() => gameActions.setGamePhase("playing")}
              className="w-full mt-8 py-4 rounded-lg bg-blue-600 text-white text-lg font-medium hover:bg-blue-700 transition-all duration-200"
            >
              Start Game
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!shuffledQuestions || shuffledQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-[#1a1f2e] p-8 pt-12 flex flex-col">
        <div className="w-full max-w-md mx-auto">
          <NavigationHeader showBack={false} />
          <p className="text-white text-center mt-8">Loading questions...</p>
        </div>
      </div>
    );
  }

  const currentQ = shuffledQuestions[currentQuestion];

  return (
    <div className="min-h-screen bg-[#1a1f2e] p-8 pt-12 flex flex-col">
      <div className="w-full max-w-md mx-auto">
        <NavigationHeader showBack={false} />

        <div className="flex justify-between items-center mb-4">
          <span className="text-white/70 text-lg">
            Question {currentQuestion + 1}/{shuffledQuestions.length}
          </span>
          <button
            onClick={() => {
              setAnswerRevealed(false);
              gameActions.previousQuestion();
            }}
            disabled={currentQuestion === 0}
            className={`text-white/70 hover:text-white transition-colors ${
              currentQuestion === 0 ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            Previous Card
          </button>
        </div>
        <div className="w-full border-b border-white/30 mb-8" />

        <div className="mb-8">
          <h2 className="text-white text-2xl font-medium mb-8">
            {currentQ.question}
          </h2>

          {answerRevealed && (
            <div className="p-6 bg-[#2a2f3e] rounded-lg border-2 border-blue-400 mb-8">
              <p className="text-white/60 text-sm mb-2">Answer</p>
              <p className="text-white text-xl font-medium">
                {currentQ.answer}
              </p>
            </div>
          )}
        </div>

        <div className="mt-auto">
          {!answerRevealed ? (
            <button
              onClick={() => setAnswerRevealed(true)}
              className="w-full py-4 rounded-lg bg-blue-600 text-white text-lg font-medium hover:bg-blue-700 transition-all duration-200"
            >
              Reveal Answer
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="w-full py-4 rounded-lg bg-blue-600 text-white text-lg font-medium hover:bg-blue-700 transition-all duration-200"
            >
              Next Card
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameInterface;
